import React from "react";
import { Page, Text, View, Document, Image, Link } from "@react-pdf/renderer";

/* Styles */
import { styles } from "./styles";

/* Helpers */
import { handleAvailability, constructObjective, constructPartnersObjective, balancePersonalDetailsPDF } from "../../helpers/candidate";
import { latestFirst, handleTimePeriod, readDob, isRealDate } from "../../helpers/date";
import { handlePartner } from "../../helpers/couple";
import { cleanProfileText } from "../../helpers/misc";

/* Components */
import { TextRow, SectionSeparator } from "./components";

export const MyDocument = ({ user, sections, accentColor, profileImageSrc }) => {

    const showSection = (key) => sections ? sections[key] !== false : true;
    const partner = user?.profile?.couple ? handlePartner(user.profile.couple, user._id) : null;
    const personalDetails = balancePersonalDetailsPDF(user.profile);
    const roleEyebrow = accentColor ? [styles.desiredPosition, { color: accentColor }] : styles.desiredPosition;
    const bulletDot  = accentColor ? [styles.bullet, { color: accentColor }] : styles.bullet;
    const photoSrc = profileImageSrc || user?.profile?.profileImage;
    
    return(
        <Document>
            <Page size={"A4"} style={styles.page}>
                <View style={styles.metaContainer}>
                    <View style={styles.metaContentContainer}>
                        <View style={styles.metaBasicInformationContainer}>
                            <Text style={styles.fullName}>{user.profile.firstName} {user.profile.lastName}</Text>
                            {user?.profile?.lookingFor?.desiredRoles[0]?.position[0]?.name ?
                                <Text style={roleEyebrow}>{user.profile.lookingFor.desiredRoles[0].position[0].name.toUpperCase()}</Text>
                            :
                                <View></View>
                            }
                            {user?.profile?.currentLocation ?
                                <TextRow propName={"Location"} value={user.profile.currentLocation.name} />
                            :
                                <View></View>
                            }
                            <TextRow propName={"Availability"} value={handleAvailability(user.profile)} />
                            <TextRow propName={"Email"} value={user.profile.email} type={"email"} />
                            {user?.profile?.primaryPhone ?
                                <TextRow propName={"Mobile Phone"} value={user.profile.primaryPhone} whatsapp={user.profile.primaryPhoneWhatsApp} />
                            :
                                <View></View>
                            }
                            {user?.profile?.secondaryPhone ?
                                <TextRow propName={"Other Phone"} value={user.profile.secondaryPhone} whatsapp={user.profile.secondaryPhoneWhatsApp} />
                            :
                                <View></View>
                            }
                            {user?.profile?.dob ?
                                <TextRow propName={"Date of Birth"} value={readDob(user.profile.dob)} />
                            :
                                <View></View>
                            }
                        </View>
                    </View>
                    {photoSrc ?
                        <View style={styles.profileImageContainer}>
                            <Image style={styles.profileImage} source={photoSrc} />
                        </View>
                    :
                        <View></View>
                    }
                </View>
                <SectionSeparator accentColor={accentColor} title={"Personal Details"} />
                <View style={styles.sectionContainer}>
                    <View style={styles.sectionListContainer}>
                        {personalDetails[0].map((detail, index) => {
                            return(
                                <View key={index}>
                                    {detail}
                                </View>
                            );
                        })}
                    </View>
                    <View style={styles.sectionListContainer}>
                        {personalDetails[1].map((detail, index) => {
                            return(
                                <View key={index}>
                                    {detail}
                                </View>
                            );
                        })}
                    </View>
                </View>
                {user?.profile?.aboutMe ?
                    <View style={styles.sectionMasterContainer}>
                        <SectionSeparator accentColor={accentColor} title={"Profile"} />
                        <View style={styles.sectionContainer}>
                            <Text style={styles.paragraph}>{cleanProfileText(user.profile.aboutMe)}</Text>
                        </View>
                    </View>
                :
                    <View></View>
                }
                {showSection("objective") && user?.profile?.lookingFor?.desiredRoles[0]?.position[0]?.name ?
                    <View style={styles.sectionMasterContainer}>
                        <SectionSeparator accentColor={accentColor} title={"Objective"} />
                        <View style={[styles.sectionContainer, { flexDirection: "column" }]}>
                            {partner?.desiredRoles?.length > 0 ?
                                <View style={{display: "flex", flexDirection: "column", paddingBottom: 10}}>
                                    <Text style={styles.paragraph}>{constructPartnersObjective(partner)}</Text>
                                </View>
                            :
                                <View></View>
                            }
                            {user.profile.lookingFor.desiredRoles.map((role, index) => {
                                if (index !== (user.profile.lookingFor.desiredRoles.length-1)) {
                                    return(
                                        <View key={index} style={{display: "flex", flexDirection: "column", paddingBottom: 10}}>
                                            <Text style={styles.paragraph}>{constructObjective(role, index, user.profile.lookingFor.desiredRoles)}</Text>
                                        </View>
                                    );
                                } else {
                                    return(
                                        <Text key={index} style={styles.paragraph}>{constructObjective(role, index, user.profile.lookingFor.desiredRoles)}</Text>
                                    );   
                                }
                            })}
                        </View>
                    </View>
                :
                    <View></View>
                }
                {(user?.profile?.qualifications.length !== 0 || user?.profile?.eng1Info?.status || user?.profile?.stcwBasicInfo?.status || user?.profile?.stcwAdvancedInfo?.status) ?
                    <View style={styles.sectionMasterContainer}>
                        <SectionSeparator accentColor={accentColor} title={"License & Qualifications"} />
                        <View style={styles.sectionContainer}>
                            <View style={styles.sectionListContainer}>
                                {user?.profile?.eng1Info?.status ?
                                    <View style={styles.bulletRow}>
                                        <Text style={bulletDot}>•</Text>
                                        <View style={styles.bulletContent}>
                                            <Text style={styles.bulletText}>ENG 1</Text>
                                            {isRealDate(user.profile?.eng1Info?.expiryDate) ?
                                                <Text style={styles.bulletDate}>(exp. {user.profile.eng1Info.expiryDate})</Text>
                                            :
                                                <View></View>
                                            }
                                        </View>
                                    </View>
                                :
                                    <View></View>
                                }
                                {user.profile.qualifications.slice(0, ((parseInt(user.profile.qualifications.length / 2))+1)).map((quality, index) => (
                                    <View style={styles.bulletRow} key={index}>
                                        <Text style={bulletDot}>•</Text>
                                        <View style={styles.bulletContent}>
                                            <Text style={styles.bulletText}>{quality.name}</Text>
                                        </View>
                                    </View>
                                ))}
                            </View>
                            <View style={styles.sectionListContainer}>
                                {user?.profile?.stcwBasicInfo?.status ?
                                    <View style={styles.bulletRow}>
                                        <Text style={bulletDot}>•</Text>
                                        <View style={styles.bulletContent}>
                                            <Text style={styles.bulletText}>Basic STCW Safety Training</Text>
                                            {isRealDate(user.profile?.stcwBasicInfo?.expiryDate) ?
                                                <Text style={styles.bulletDate}>(exp. {user.profile.stcwBasicInfo.expiryDate})</Text>
                                            :
                                                <View></View>
                                            }
                                        </View>
                                    </View>
                                :
                                    <View></View>
                                }
                                {user?.profile?.stcwAdvancedInfo?.status ?
                                    <View style={styles.bulletRow}>
                                        <Text style={bulletDot}>•</Text>
                                        <View style={styles.bulletContent}>
                                            <Text style={styles.bulletText}>Advanced STCW Safety Training</Text>
                                            {isRealDate(user.profile?.stcwAdvancedInfo?.expiryDate) ?
                                                <Text style={styles.bulletDate}>(exp. {user.profile.stcwAdvancedInfo.expiryDate})</Text>
                                            :
                                                <View></View>
                                            }
                                        </View>
                                    </View>
                                :
                                    <View></View>
                                }
                                {user.profile.qualifications.slice((parseInt(user.profile.qualifications.length / 2))+1).map((quality, index) => (
                                    <View style={styles.bulletRow} key={index}>
                                        <Text style={bulletDot}>•</Text>
                                        <View style={styles.bulletContent}>
                                            <Text style={styles.bulletText}>{quality.name}</Text>
                                        </View>
                                    </View>
                                ))}
                            </View>
                        </View>
                    </View>
                :
                    <View></View>
                }
                {user?.profile?.pastRoles.length !== 0 ?
                    <View style={styles.sectionMasterContainer}>
                        <SectionSeparator accentColor={accentColor} title={"Experience"} />
                        <View style={styles.sectionContainer}>
                            <View style={styles.sectionListContainer}>
                                {user.profile.pastRoles.sort(latestFirst).map((role, index) => {
                                    return(
                                        <View style={styles.listItemContainer} key={index} wrap={false}>
                                            <View style={styles.sectionItemContentContainer}>
                                                <View style={styles.sectionItemContentTitleContainer}>
                                                    <View style={styles.timelineTitleMain}>
                                                        <Text style={styles.timelineRole}>{role.position}</Text>
                                                        {role.companyName ?
                                                            <Text style={styles.timelineCompany}>{role.companyName}</Text>
                                                        :
                                                            <View></View>
                                                        }
                                                    </View>
                                                    <View style={styles.timelineDateContainer}>
                                                        <Text style={styles.timelineDate}>{handleTimePeriod(role.fromDate, role.toDate, role.present)}</Text>
                                                    </View>
                                                </View>
                                                {role.description ?
                                                    <View style={styles.sectionItemContentDescriptionContainer}>
                                                        <Text style={styles.sectionItemContentDescription}>{cleanProfileText(role.description)}</Text>
                                                    </View>
                                                :
                                                    <View></View>
                                                }
                                            </View>
                                        </View>
                                    );
                                })}
                            </View>
                        </View>
                    </View>
                :
                    <View></View>
                }
                {user?.profile?.education.length !== 0 ?
                    <View style={styles.sectionMasterContainer}>
                        <SectionSeparator accentColor={accentColor} title={"Education"} />
                        <View style={styles.sectionContainer}>
                            <View style={styles.sectionListContainer}>
                                {user.profile.education.sort(latestFirst).map((edu, index) => {
                                    return(
                                        <View style={styles.listItemContainer} key={index} wrap={false}>
                                            <View style={styles.sectionItemContentContainer}>
                                                <View style={styles.sectionItemContentTitleContainer}>
                                                    <View style={styles.timelineTitleMain}>
                                                        <Text style={styles.timelineRole}>{edu.name}</Text>
                                                    </View>
                                                    <View style={styles.timelineDateContainer}>
                                                        <Text style={styles.timelineDate}>{handleTimePeriod(edu.fromDate, edu.toDate, edu.present, true)}</Text>
                                                    </View>
                                                </View>
                                                {edu.description ?
                                                    <View style={styles.sectionItemContentDescriptionContainer}>
                                                        <Text style={styles.sectionItemContentDescription}>{cleanProfileText(edu.description)}</Text>
                                                    </View>
                                                :
                                                    <View></View>
                                                }
                                            </View>
                                        </View>
                                    );
                                })}
                            </View>
                        </View>
                    </View>
                :
                    <View></View>
                }
                {user?.profile?.skills.length !== 0 ?
                    <View style={styles.sectionMasterContainer}>
                        <SectionSeparator accentColor={accentColor} title={"Skills"} />
                        <View style={styles.sectionContainer}>
                            <View style={styles.sectionListContainer}>
                                {user.profile.skills.slice(0, ((parseInt(user.profile.skills.length / 2))+1)).map((skill, index) => (
                                    <View style={styles.bulletRow} key={index}>
                                        <Text style={bulletDot}>•</Text>
                                        <View style={styles.bulletContent}>
                                            <Text style={styles.bulletText}>{skill}</Text>
                                        </View>
                                    </View>
                                ))}
                            </View>
                            <View style={styles.sectionListContainer}>
                                {user.profile.skills.slice((parseInt(user.profile.skills.length / 2))+1).map((skill, index) => (
                                    <View style={styles.bulletRow} key={index}>
                                        <Text style={bulletDot}>•</Text>
                                        <View style={styles.bulletContent}>
                                            <Text style={styles.bulletText}>{skill}</Text>
                                        </View>
                                    </View>
                                ))}
                            </View>
                        </View>
                    </View>
                :
                    <View></View>
                }
                {showSection("equipment") && user?.profile?.equipment.length !== 0 ?
                    <View style={styles.sectionMasterContainer}>
                        <SectionSeparator accentColor={accentColor} title={"Equipment Knowledge"} />
                        <View style={styles.sectionContainer}>
                            <View style={styles.sectionListContainer}>
                                {user.profile.equipment.slice(0, ((parseInt(user.profile.equipment.length / 2))+1)).map((item, index) => (
                                    <View style={styles.bulletRow} key={index}>
                                        <Text style={bulletDot}>•</Text>
                                        <View style={styles.bulletContent}>
                                            <Text style={styles.bulletText}>{item}</Text>
                                        </View>
                                    </View>
                                ))}
                            </View>
                            <View style={styles.sectionListContainer}>
                                {user.profile.equipment.slice((parseInt(user.profile.equipment.length / 2))+1).map((item, index) => (
                                    <View style={styles.bulletRow} key={index}>
                                        <Text style={bulletDot}>•</Text>
                                        <View style={styles.bulletContent}>
                                            <Text style={styles.bulletText}>{item}</Text>
                                        </View>
                                    </View>
                                ))}
                            </View>
                        </View>
                    </View>
                :
                    <View></View>
                }
                {showSection("hobbies") && user?.profile?.hobbies ?
                    <View style={styles.sectionMasterContainer}>
                        <SectionSeparator accentColor={accentColor} title={"Interests & Hobbies"} />
                        <View style={styles.sectionContainer}>
                            <Text style={styles.paragraph}>{cleanProfileText(user.profile.hobbies)}</Text>
                        </View>
                    </View>
                :
                    <View></View>
                }
                {showSection("references") && user?.profile?.references.length !== 0 ?
                    <View style={styles.sectionMasterContainer}>
                        <SectionSeparator accentColor={accentColor} title={"References"} />
                        <View style={styles.sectionContainer}>
                            <View style={styles.sectionListContainer}>
                                {user.profile.references.map((reference, index) => {
                                    const personParts = [reference.referenceName, reference.position].filter(Boolean);
                                    return(
                                        <View style={styles.referenceItemContainer} key={index} wrap={false}>
                                            {reference.companyName ?
                                                <Text style={styles.referenceCompany}>{reference.companyName}</Text>
                                            : null}
                                            {personParts.length > 0 ?
                                                <Text style={styles.referencePerson}>{personParts.join(" · ")}</Text>
                                            : null}
                                            {(reference.email || reference.contactNumber) ?
                                                <View style={styles.referenceContactRow}>
                                                    {reference.email ?
                                                        <Link style={styles.referenceContactLink} href={`mailto:${reference.email}`}>{reference.email}</Link>
                                                    : null}
                                                    {reference.email && reference.contactNumber ?
                                                        <Text style={styles.referenceContactSep}>·</Text>
                                                    : null}
                                                    {reference.contactNumber ?
                                                        <Text style={styles.referenceContact}>{reference.contactNumber}</Text>
                                                    : null}
                                                </View>
                                            : null}
                                        </View>
                                    );
                                })}
                            </View>
                        </View>
                    </View>
                :
                    <View></View>
                }
            </Page>
        </Document>
    );

}